import { PERSONA_INITIAL_OBJECT } from '@/config/settings';
import useElysiaStore from '@/app/elysia-store';
import { getBrandDetails } from '@/services/brand';
import notify from '@/services/notify';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
interface BrandAgentPreferencesProps {
  savePreferences?: boolean;
  brandId?: string;
  setCanSave?: (arg: boolean) => void;
  navigate?: boolean;
  handleClose?: () => void;
}
const BrandAgentPreferences = ({
  savePreferences = false,
  setCanSave,
  navigate = false,
  handleClose,
  brandId,
}: BrandAgentPreferencesProps) => {
  const [allPersonas, setAllPersonas] = useState<any>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [tempDivision, setTempDivision] = useState<string>('');
  const [tempBrand, setTempBrand] = useState<Brand>({ id: '', name: '' });
  const [tempPersona, setTempPersona] = useState<Persona>(PERSONA_INITIAL_OBJECT);

  const division = useElysiaStore((state) => state.division);
  const brand = useElysiaStore((state) => state.brand);
  const persona = useElysiaStore((state) => state.persona);
  const agentAlreadyExists = useElysiaStore((state) => state.agentAlreadyExists);
  const isPreview = useElysiaStore((state) => state.isPreview);
  const previewPersonas = useElysiaStore((state) => state.previewPersonas);
  const allDivisions = useElysiaStore((state) => state.allDivisions);
  const setDivision = useElysiaStore.getState().setDivision;
  const setBrand = useElysiaStore.getState().setBrand;
  const setPersona = useElysiaStore.getState().setPersona;
  const setBrandDetails = useElysiaStore.getState().setBrandDetails;
  const setAgentAlreadyExists = useElysiaStore.getState().setAgentAlreadyExists;
  const setSelectedPreviewPersona = useElysiaStore.getState().setSelectedPreviewPersona;

  const nav = useNavigate();
  const handleDivisionChange = (e) => {
    setTempDivision(e.target.value);
    setTempBrand({ id: '', name: '' });
    setCanSave?.(false);
    setTempPersona(PERSONA_INITIAL_OBJECT);
    setAllPersonas([]);
  };

  const handleBrandChange = async (e) => {
    setTempPersona(PERSONA_INITIAL_OBJECT);
    setAllPersonas([]);
    setTempBrand(allBrands.find((brand) => brand.id === e.target.value) ?? { id: '', name: '' });
  };

  const handlePersonaChange = (e) => {
    const persona = allPersonas.find((persona) => persona.id === e.target.value);
    setTempPersona(persona);
    if (isPreview) {
      setSelectedPreviewPersona(persona);
    }
  };

  useEffect(() => {
    // to get al the brands in the division
    setAllBrands(allDivisions?.find((div) => div.division === tempDivision)?.brand);
    //eslint-disable-next-line
  }, [tempDivision]);

  useEffect(() => {
    // to get the personas and to set the other brand related details in the context
    const fetchPersonas = async () => {
      const res = await getBrandDetails(tempBrand.id);
      if (res?.agentAlreadyExists == false) {
        setAgentAlreadyExists(false);
        const message = ` Sorry no tone of voice created for the selected brand`;
        const type = 'error';
        notify({
          message,
          type,
        });
      } else {
        setAgentAlreadyExists(true);
        setAllPersonas(res?.communication?.target_audiences);
        setBrandDetails(res);
      }
    };
    if (tempBrand?.id?.length && !isPreview) {
      fetchPersonas();
    } else if (!tempBrand?.id?.length) {
      setTempPersona(PERSONA_INITIAL_OBJECT);
      setCanSave?.(false);
    }
    //eslint-disable-next-line
  }, [tempBrand]);

  useEffect(() => {
    // to check if start conversation/update button is active or not
    setCanSave?.(false);
    if (tempDivision?.length && tempBrand?.id?.length && (agentAlreadyExists || isPreview)) {
      setCanSave?.(true);
    }
    //eslint-disable-next-line
  }, [tempBrand, tempDivision, agentAlreadyExists]);

  useEffect(() => {
    // to set the temporary feilds back to the context values on mount
    setTempBrand(brand);
    setTempDivision(division);
    if (!isPreview) {
      setTempPersona(persona);
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setAllPersonas(previewPersonas);
  }, [previewPersonas]);

  useEffect(() => {
    // the event which triggers on start coneversation/update button click
    if (savePreferences) {
      setDivision(tempDivision);
      setPersona(tempPersona);
      setBrand(tempBrand);
      if (navigate) {
        nav(`/?app=${brandId}`); // if start conversation
      } else {
        handleClose?.(); // if update from the modal
      }
    }

    //eslint-disable-next-line
  }, [savePreferences]);

  return (
    <div className="preferences-dropdowns">
      <div>
        <label className="mt-2">
          Division<span className="required">*</span>
        </label>
        <Form.Select
          aria-label="Select Division"
          onChange={handleDivisionChange}
          data-testid="division-dropdown"
          autoComplete="off"
          disabled={isPreview}
          value={tempDivision}
        >
          <option value="">Please select a division</option>
          {allDivisions?.length &&
            allDivisions?.map((division, idx) => (
              <option key={idx} value={division.division}>
                {division.division}
              </option>
            ))}
        </Form.Select>
      </div>
      <div>
        <label className="mt-2">
          Brand<span className="required">*</span>
        </label>
        <Form.Select
          aria-label="Select Brand"
          onChange={handleBrandChange}
          data-testid="brand-dropdown"
          autoComplete="off"
          value={tempBrand.id}
          disabled={!tempDivision.length || isPreview}
        >
          <option value="">Please select a brand</option>
          {allBrands?.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </Form.Select>
      </div>
      <div>
        <label className="mt-2">Persona</label>
        <Form.Select
          aria-label="Select Persona"
          onChange={handlePersonaChange}
          data-testid="persona-dropdown"
          autoComplete="off"
          value={tempPersona?.id}
          disabled={!allPersonas?.length}
        >
          <option value="">Please select an optional persona</option>
          {allPersonas?.length &&
            allPersonas?.map((persona) => (
              <option key={persona?.id} value={persona?.id}>
                {persona?.name}
              </option>
            ))}
        </Form.Select>
      </div>
    </div>
  );
};

export default BrandAgentPreferences;
